import { useState } from 'react';
import { Check, Link2, Mail, Share2, Tags } from 'lucide-react';

import { useI18n } from '@/shared/contexts/I18nProvider';
import { getAbsoluteUrl } from '@/shared/hooks';

import type { LocalizedBlogPost } from './blogPosts';

type BlogArticleFooterProps = {
  post: LocalizedBlogPost;
};

const shareActionClassName = [
  'inline-flex h-10 items-center justify-center gap-2 rounded-full',
  'border border-border-primary bg-background-primary px-3',
  'font-mono text-xs text-text-secondary transition-colors',
  'hover:border-accent-primary/50 hover:text-accent-primary',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus'
].join(' ');

const copyText = async (value: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
};

const BlogArticleFooter = ({ post }: BlogArticleFooterProps) => {
  const { content } = useI18n();
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const shareUrl = post.canonicalUrl || getAbsoluteUrl(post.canonicalPath);
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(post.title);
  const tags = Array.from(
    new Set([post.service, post.industry, post.category].filter(Boolean))
  );

  const handleCopyLink = async () => {
    try {
      await copyText(shareUrl);
      setIsLinkCopied(true);
    } catch {
      setIsLinkCopied(false);
    }
  };

  return (
    <footer className="mt-12 grid gap-6 border-y border-border-muted py-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
      <div>
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-text-tertiary">
          <Tags className="h-4 w-4 text-accent-primary" aria-hidden="true" />
          {content.blog.tags}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {tags.map(tag => (
            <span
              key={tag}
              className="rounded-full border border-border-muted bg-background-card px-3 py-1.5 font-mono text-xs text-text-secondary"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      <div>
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-text-tertiary lg:justify-end">
          <Share2 className="h-4 w-4 text-accent-primary" aria-hidden="true" />
          {content.blog.share}
        </p>
        <div className="mt-3 flex flex-wrap gap-2 lg:justify-end">
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className={shareActionClassName}
            aria-label={content.blog.shareOnLinkedIn}
            title={content.blog.shareOnLinkedIn}
          >
            <span className="text-sm font-semibold" aria-hidden="true">
              in
            </span>
            <span>LinkedIn</span>
          </a>
          <a
            href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
            target="_blank"
            rel="noopener noreferrer"
            className={shareActionClassName}
            aria-label={content.blog.shareOnX}
            title={content.blog.shareOnX}
          >
            <span className="text-sm font-semibold" aria-hidden="true">
              X
            </span>
            <span className="sr-only">{content.blog.shareOnX}</span>
          </a>
          <a
            href={`mailto:?subject=${encodedTitle}&body=${encodeURIComponent(`${post.title}\n\n${shareUrl}`)}`}
            className={shareActionClassName}
            aria-label={content.blog.shareByEmail}
            title={content.blog.shareByEmail}
          >
            <Mail className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">{content.blog.shareByEmail}</span>
          </a>
          <button
            type="button"
            className={shareActionClassName}
            onClick={() => void handleCopyLink()}
            aria-label={
              isLinkCopied ? content.blog.linkCopied : content.blog.copyLink
            }
            title={
              isLinkCopied ? content.blog.linkCopied : content.blog.copyLink
            }
          >
            {isLinkCopied ? (
              <Check
                className="h-4 w-4 text-accent-success"
                aria-hidden="true"
              />
            ) : (
              <Link2 className="h-4 w-4" aria-hidden="true" />
            )}
            <span>
              {isLinkCopied ? content.blog.linkCopied : content.blog.copyLink}
            </span>
          </button>
        </div>
      </div>
    </footer>
  );
};

export default BlogArticleFooter;
